/**
 * Die klappbaren Gruppen der Projektübersicht — Bauprojekte nach Phase in
 * Arbeitsreihenfolge (Abgeschlossen und Verloren zugeklappt), Pflegeprojekte
 * nach Status. Darin je nach Ansicht Kacheln oder Listenzeilen.
 * Beim Suchen ist alles aufgeklappt — wer sucht, will den Treffer sehen.
 */
import type { Dispatch, SetStateAction } from "react";
import type { ProjektUebersicht } from "../../lib/types";
import type { ProjektPhaseZeile } from "../../lib/projektLage";
import { PFLEGE_STATUS_ORDER, STATUS_LABEL, STATUS_ORDER, canonicalProjektStatus, fmtEUR, statusIdFor } from "../../lib/projekte";
import { nachPhase } from "../../lib/phasenGruppen";
import ProjektKarte, { type StatusWahl } from "./ProjektKarte";
import ProjektZeile from "./ProjektZeile";

type Gruppe = { schluessel: string; titel: string; punkt: string; items: ProjektUebersicht[]; offen: boolean; ueberfaellig: number };

type Props = {
  items: ProjektUebersicht[];
  art: "bau" | "pflege";
  phasen?: Record<string, ProjektPhaseZeile>;
  wahl: StatusWahl;
  collapsed: Record<string, boolean>;
  setCollapsed: Dispatch<SetStateAction<Record<string, boolean>>>;
  alleOffen: boolean;
  liste: boolean;
  leerText: string;
};

function gruppieren({ items, art, phasen, wahl, collapsed }: Props): Gruppe[] {
  if (art === "bau" && phasen) {
    return nachPhase(items, phasen).map((g) => {
      const k = `phase:${g.key}`;
      return {
        schluessel: k, titel: g.titel, punkt: "ph-" + g.key, items: g.items, ueberfaellig: g.ueberfaellig,
        offen: collapsed[k] === undefined ? !g.zu : !collapsed[k],
      };
    });
  }
  const map: Record<string, ProjektUebersicht[]> = {};
  for (const p of items) {
    const s = canonicalProjektStatus(p.status || "Unbekannt", wahl.optionen);
    (map[s] ||= []).push(p);
  }
  const order = art === "pflege" ? PFLEGE_STATUS_ORDER : STATUS_ORDER;
  const reihe = [...order.filter((s) => map[s]), ...Object.keys(map).filter((s) => !order.includes(s))];
  return reihe.map((s) => {
    const sid = statusIdFor(s);
    return { schluessel: s, titel: STATUS_LABEL[sid] || s, punkt: "s-" + sid, items: map[s], ueberfaellig: 0, offen: !collapsed[s] };
  });
}

export default function ProjektGruppen(props: Props) {
  const { phasen, wahl, setCollapsed, alleOffen, liste, leerText } = props;
  const gruppen = gruppieren(props);
  if (gruppen.length === 0) return <div className="vg-empty">{leerText}</div>;
  return (
    <>
      {gruppen.map((g) => {
        const offen = alleOffen || g.offen;
        const sum = g.items.reduce((a, p) => a + Number(p.nettowert || 0), 0);
        return (
          <div className={"vg-status-group" + (liste ? " is-liste" : "")} key={g.schluessel}>
            <button
              className="vg-status-group-head"
              type="button"
              aria-expanded={offen}
              onClick={() => setCollapsed((prev) => ({ ...prev, [g.schluessel]: offen }))}
            >
              <span className={"vg-status-dot " + g.punkt} />
              <span className="vg-status-group-title">{g.titel}</span>
              <span className="vg-status-group-count">
                {g.items.length} {g.items.length === 1 ? "Projekt" : "Projekte"}
                {g.ueberfaellig > 0 && <span className="ph-marke is-rot">{g.ueberfaellig} überfällig</span>}
              </span>
              <span className="vg-status-group-sum">Σ {fmtEUR(sum) || "—"}</span>
              <span className="vg-status-group-chev" data-open={offen}>▸</span>
            </button>
            {offen && (liste ? (
              <div className="vg-proj-liste">
                {g.items.map((p) => <ProjektZeile key={p.id} p={p} phase={phasen?.[p.id]} wahl={wahl} />)}
              </div>
            ) : (
              <div className="vg-proj-grid">
                {g.items.map((p) => <span key={p.id}><ProjektKarte p={p} phase={phasen?.[p.id]} wahl={wahl} /></span>)}
              </div>
            ))}
          </div>
        );
      })}
    </>
  );
}
